// frontend/src/components/admin/reseller/ResellerTable.js
import React from "react";
import { Box, IconButton, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { AccountBalance, Delete, Edit } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";

const ResellerTable = ({ resellers, loading, onEdit, onCredit, onDelete }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Enhanced safe getter with better null checks
  const safeGet = (params, field, defaultValue = "") => {
    if (!params?.row) return defaultValue;
    const value = params.row[field];
    if (value === null || value === undefined) return defaultValue;
    return value;
  };

  const columns = [
    {
      field: "username", // This is the data field
      headerName: t("userForm.fields.username"),
      width: 150,
      renderCell: (params) => (
        <Typography
          component="a"
          sx={{
            cursor: "pointer",
            "&:hover": { textDecoration: "underline" },
          }}
          onClick={() => navigate(`/admin/resellers/${params.row.id}`)}
        >
          {safeGet(params, "username", "")}
        </Typography>
      ),
    },
    {
      field: "email",
      headerName: t("userForm.fields.email"),
      width: 200,
      renderCell: (params) => safeGet(params, "email", ""),
    },
    {
      field: "creditBalance",
      headerName: t("credits.balance"),
      width: 120,
      renderCell: (params) => {
        const balance = safeGet(params, "creditBalance", 0);
        return new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: "USD",
          minimumFractionDigits: 2,
        }).format(
          typeof balance === "string" ? parseFloat(balance) || 0 : balance
        );
      },
    },
    {
      field: "markupPercentage",
      headerName: t("userForm.fields.markupPercentage"),
      width: 100,
      renderCell: (params) => {
        const markup = safeGet(params, "markupPercentage", 0);
        const value =
          typeof markup === "string" ? parseFloat(markup) || 0 : markup;
        return `${value.toFixed(2)}%`;
      },
    },
    // {
    //   field: "username",
    //   headerName: t("userForm.fields.username"),
    //   width: 150,
    //   renderCell: (params) => (
    //     <Typography
    //       component="a"
    //       sx={{
    //         cursor: "pointer",
    //         "&:hover": { textDecoration: "underline" },
    //       }}
    //       onClick={() => navigate(`/admin/resellers/${params.row.id}`)}
    //     >
    //       {safeGet(params, "username", "")}
    //     </Typography>
    //   ),
    // },
    {
      field: "allowedPackages",
      headerName: t("userForm.fields.allowedPackages"),
      width: 150,
      renderCell: (params) => {
        const packages = safeGet(params, "allowedPackages", []);
        const count = Array.isArray(packages) ? packages.length : 0;
        return t("packages.countFormat", { count });
      },
    },
    {
      field: "packagePricing",
      headerName: t("packages.customPricing"),
      width: 150,
      renderCell: (params) => {
        const pricing = safeGet(params, "packagePricing", {});
        const count = Object.keys(pricing || {}).length;
        return t("packages.customPriceCount", { count });
      },
    },
    {
      field: "actions",
      headerName: t("common.actions.actions"),
      width: 150,
      sortable: false,
      renderCell: (params) => (
        <Box>
          <IconButton
            onClick={() => onEdit(params.row)}
            size="small"
            title={t("reseller.actions.edit")}
          >
            <Edit />
          </IconButton>
          <IconButton
            onClick={() => onCredit(params.row)}
            size="small"
            title={t("reseller.actions.adjustCredits")}
          >
            <AccountBalance />
          </IconButton>
          <IconButton
            onClick={() => onDelete(params.row?.id)}
            size="small"
            title={t("reseller.actions.delete")}
          >
            <Delete />
          </IconButton>
        </Box>
      ),
    },
  ];

  // Ensure we have valid reseller data
  const processedResellers = React.useMemo(() => {
    if (!Array.isArray(resellers)) return [];
    return resellers.map((reseller) => ({
      id: reseller?.id || Math.random().toString(),
      username: reseller?.username || "",
      email: reseller?.email || "",
      creditBalance: reseller?.creditBalance || 0,
      markupPercentage: reseller?.markupPercentage || 0,
      allowedPackages: reseller?.allowedPackages || [],
      packagePricing: reseller?.packagePricing || {},
    }));
  }, [resellers]);

  React.useEffect(() => {
    console.debug("Processed resellers data:", processedResellers);
  }, [processedResellers]);

  return (
    <DataGrid
      rows={processedResellers}
      columns={columns}
      pageSize={5}
      rowsPerPageOptions={[5, 10, 25]}
      loading={loading}
      disableSelectionOnClick
      autoHeight
      error={null}
      sx={{
        "& .MuiDataGrid-cell": {
          whiteSpace: "normal",
          lineHeight: "normal",
        },
      }}
    />
  );
};

export default ResellerTable;
